"use client"

import { useState, useCallback } from "react"
import SelectInput from "@/shared/components/inputs/select-input"
import { useFormContext, Controller } from "react-hook-form"
import { useDirtyFields } from "../context/dirty-fields-context"
import { useReadOnly } from "../context/read-only-context"
import { HomeWithHomeOwnerFormType } from "../schema/home-with-home-owner.schema"
import { overallStatusOptions, overallStatusSelectableOptions } from "../constants/options"
import { displayOverallStatusLabel, normalizeOverallStatus } from "../lib/overall-status.utils"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

type OverallStatusValue = (typeof overallStatusOptions)[number]["value"]
import { Label } from "@/shared/components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog"
import { Button } from "@/shared/components/ui/button"
import { Info } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import { WORKSCOPE_PATHS_FOR_OVERALL_STATUS_CHECK, SECTION_LABEL_TO_WORK_SCOPE_SLUG } from "../lib/workscope-status-paths-for-overall-check"
import { useActiveWorkScopes } from "../hooks/use-active-work-scopes"

const OVERALL_STATUS_HEADING = "Overall Relief & Rebuilding Recovery Status"

const OVERALL_STATUS_DESCRIPTIONS: Array<{ label?: string; text: string }> = [
  { text: "Status options represent the current stage of relief and rebuilding recovery work on the home." },
  { label: "Not Started", text: "The home has been identified for assistance, but no work has begun." },
  { label: "In Progress", text: "Active relief and/or rebuilding work is currently being performed on the home." },
  { label: "Functionally Complete", text: "The home is safe, sanitary, and livable for the homeowner, with at least a functional kitchen and at least one functional bathroom, though minor work or finishing touches (such as interior painting, trim, etc.) may remain." },
  { label: "Complete", text: "All agreed scope of relief and/or rebuilding recovery work has been finished." },
  { label: "No Assistance Required", text: "The home does not require assistance from any relief and/or rebuilding organization represented in ReliefHub or is outside the scope of those organizations." },
  { label: "Homeowner Responsible (HOR)", text: "The homeowner completes the work themselves or is working with another organization, contractor, etc. that is not represented in ReliefHub to complete the work." },
  { label: "Materials Only", text: "An organization represented in ReliefHub is providing materials only; labor will be completed by the homeowner, another organization, or a contractor." },
  { label: "Materials Only Complete", text: "Materials have been delivered and the materials-only support for this home is finished." },
  {
    label: "Demo (Demolition)",
    text: "The home has been determined to be uninhabitable and designated for demolition. No relief and/or rebuilding recovery work will be performed on the structure.",
  },
  { text: "Status is a manual entry by Base Leaders and/or Admin." },
]

/** Overall status values that count as "complete" and trigger the incomplete-workscope confirmation. */
const COMPLETE_OVERALL_STATUSES = ["Functionally Complete", "Complete", "Materials Only Complete", "100%"]

/** Workscope statuses that count as incomplete (trigger the popup when user sets Overall Status to a complete value). */
const INCOMPLETE_WORKSCOPE_STATUSES = ["Unknown", "25%", "50%", "75%", "Materials Only", "Needed Not Started"]

/** Sections where we only flag blank/Unknown (other statuses like 25%, Materials Only, etc. are OK for complete Overall Status). */
const SECTIONS_ONLY_FLAG_IF_BLANK = ["Miscellaneous Goodwill"]

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current: unknown, key) => (current as Record<string, unknown>)?.[key], obj)
}

function isWorkscopeStatusBlankOrUnknown(value: unknown): boolean {
  if (value == null) return true
  if (typeof value !== "string") return true
  const s = value.trim()
  return s === "" || s === "Unknown"
}

function isWorkscopeStatusIncomplete(value: unknown): boolean {
  if (value == null) return true
  if (typeof value !== "string") return true
  const s = value.trim()
  return s === "" || INCOMPLETE_WORKSCOPE_STATUSES.includes(s)
}

/**
 * Returns section labels (accordion headings) that have at least one status path incomplete
 * (blank, Unknown, 25%, 50%, 75%, Materials Only, or Needed Not Started).
 * Only includes sections whose work scope is active for the region (when activeWorkScopes is provided and non-empty).
 */
function getIncompleteWorkscopeSections(
  formValues: unknown,
  activeWorkScopes: Record<string, boolean> | null
): string[] {
  const incompleteLabels = new Set<string>()
  const hasActiveFilter = activeWorkScopes && Object.keys(activeWorkScopes).length > 0

  for (const { path, sectionLabel } of WORKSCOPE_PATHS_FOR_OVERALL_STATUS_CHECK) {
    if (hasActiveFilter) {
      const slug = SECTION_LABEL_TO_WORK_SCOPE_SLUG[sectionLabel]
      if (slug != null && activeWorkScopes[slug] !== true) continue
    }
    const value = getNestedValue(formValues, path)
    const isIncomplete = SECTIONS_ONLY_FLAG_IF_BLANK.includes(sectionLabel)
      ? isWorkscopeStatusBlankOrUnknown(value)
      : isWorkscopeStatusIncomplete(value)
    if (isIncomplete) incompleteLabels.add(sectionLabel)
  }
  return Array.from(incompleteLabels).sort()
}

interface CompletionStatusDisplayProps {
  className?: string
}

const OVERALL_STATUS_FIELD = "home.homeStatusInfo.overallStatus"

function CompletionStatusDisplay({ className }: CompletionStatusDisplayProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingOverallStatus, setPendingOverallStatus] = useState<OverallStatusValue | null>(null)
  const [incompleteSections, setIncompleteSections] = useState<string[]>([])
  const { watch, control, getValues, setValue } = useFormContext<HomeWithHomeOwnerFormType>()
  const { isFieldDirty } = useDirtyFields()
  const { isReadOnly } = useReadOnly()
  const { session } = useSession()
  const { activeWorkScopes } = useActiveWorkScopes()
  const isDirty = isFieldDirty(OVERALL_STATUS_FIELD)
  const isHomeowner = session?.role === UserRole.HOMEOWNER
  const isOverallStatusDisabled = isReadOnly || isHomeowner
  
  const overallStatusDisplay = displayOverallStatusLabel(watch(OVERALL_STATUS_FIELD))

  const handleOverallStatusChange = useCallback(
    (newValue: string, fieldOnChange: (value: string) => void) => {
      if (!COMPLETE_OVERALL_STATUSES.includes(newValue)) {
        fieldOnChange(newValue)
        return
      }
      const incomplete = getIncompleteWorkscopeSections(getValues(), activeWorkScopes)
      if (incomplete.length === 0) {
        fieldOnChange(newValue)
        return
      }
      setPendingOverallStatus(newValue as OverallStatusValue)
      setIncompleteSections(incomplete)
      setConfirmDialogOpen(true)
    },
    [getValues, activeWorkScopes]
  )

  const handleConfirmSetAnyway = useCallback(() => {
    if (pendingOverallStatus) {
      setValue(OVERALL_STATUS_FIELD, pendingOverallStatus, { shouldDirty: true })
    }
    setPendingOverallStatus(null)
    setIncompleteSections([])
    setConfirmDialogOpen(false)
  }, [pendingOverallStatus, setValue])

  const handleConfirmCancel = useCallback(() => {
    setPendingOverallStatus(null)
    setIncompleteSections([])
    setConfirmDialogOpen(false)
  }, [])

  // Define color coding based on status
  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "No Assistance Required":
      case "Complete":
      case "Materials Only Complete":
      case "100%":
      case "HOR":
      case "Demo":
        return "bg-green-100 border-green-300 text-green-800"
      case "In Progress":
        return "bg-yellow-100 border-yellow-300 text-yellow-800"
      case "Functionally Complete":
        return "bg-orange-100 border-orange-300 text-orange-800"
      case "Not Started":
        return "bg-gray-100 border-gray-300 text-gray-800"
      default:
        return "" // Use default form field styling when no status is set
    }
  }

  const dropdownColorClass = getStatusColor(overallStatusDisplay)

  return (
    <div className={cn("space-y-2 w-full", className)}>
      <div className="flex items-center gap-1.5">
        <Label className="form-label transition-colors duration-200">Overall Relief &amp; Rebuilding Recovery Status</Label>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    aria-label="Overall Home Status options"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={8}
                className="w-[min(94vw,420px)] max-h-[40vh] md:max-h-[65vh] overflow-y-auto text-[11px] leading-tight"
              >
            <p className="font-semibold mb-1">{OVERALL_STATUS_HEADING}</p>
                <div className="space-y-1">
                  {OVERALL_STATUS_DESCRIPTIONS.map((item, idx) => (
                    <p key={`${item.label ?? "line"}-${idx}`} className={!item.label && idx === OVERALL_STATUS_DESCRIPTIONS.length - 1 ? "italic" : undefined}>
                      {item.label ? (
                        <>
                          <span className="font-semibold">{item.label}</span>
                          {" - "}
                          {item.text}
                        </>
                      ) : (
                        item.text
                      )}
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <PopoverContent
            side="bottom"
            align="start"
            sideOffset={8}
            className="w-[min(94vw,440px)] max-h-[42vh] md:max-h-[72vh] overflow-y-auto text-xs leading-tight"
          >
            <p className="font-semibold mb-1">{OVERALL_STATUS_HEADING}</p>
            <div className="space-y-1.5">
              {OVERALL_STATUS_DESCRIPTIONS.map((item, idx) => (
                <p key={`${item.label ?? "line-pop"}-${idx}`} className={!item.label && idx === OVERALL_STATUS_DESCRIPTIONS.length - 1 ? "italic" : undefined}>
                  {item.label ? (
                    <>
                      <span className="font-semibold">{item.label}</span>
                      {" - "}
                      {item.text}
                    </>
                  ) : (
                    item.text
                  )}
                </p>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Controller
        name={OVERALL_STATUS_FIELD}
        control={control}
        render={({ field }) => (
          <SelectInput
            name={field.name}
            value={normalizeOverallStatus(field.value)}
            onChange={(value) => handleOverallStatusChange(value, field.onChange)}
            onBlur={field.onBlur}
            ref={field.ref}
            options={overallStatusSelectableOptions}
            disabled={isOverallStatusDisabled}
            className={`text-sm transition-colors duration-200 ${dropdownColorClass}`}
            inputGroupProps={{
              wrapperClassName: "mb-0",
              isDirty,
            }}
          />
        )}
      />
      <Dialog open={confirmDialogOpen} onOpenChange={(open) => !open && handleConfirmCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Status all workscopes first?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The following workscopes do not have a final status. Please complete or set a final status for each (no blank, Unknown, 25%, 50%, 75%, Materials Only, or Needed Not Started) before marking Overall Home Status as complete.
          </p>
          <ul className="text-sm list-disc list-inside max-h-48 overflow-y-auto pr-2">
            {incompleteSections.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <p className="text-sm font-medium">
            Do you want to set Overall Home Status to &quot;{pendingOverallStatus}&quot; anyway?
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleConfirmCancel}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirmSetAnyway}>
              Set anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CompletionStatusDisplay
